import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useUpdate from "../../../../api/useUpdate";
import useFetchObjects from "../../../../api/useFetchObjects";

const EditExpensePaymentListDialog = ({ open, onClose, payment, token }) => {
  const { t } = useTranslation();

  const [expense_source, setExpenseSource] = useState(
    payment?.expense_source || ""
  );
  const [amount, setAmount] = useState(payment?.amount || "");
  const [date, setDate] = useState(payment?.date || "");
  const [formErrors, setFormErrors] = useState({});

  const { data: sources, loading: loadingExpenseSources } = useFetchObjects(
    ["expense-sources"],
    "expense-sources/",
    token
  );

  const { handleUpdate, loading } = useUpdate("expense-payments", token);

  const validateForm = () => {
    const errors = {};
    if (!expense_source) errors.expense_source = t("finance.expenseSource");
    if (!amount || amount <= 0)
      errors.amount = t("finance.validAmountRequired");
    if (!date) errors.date = t("finance.dateRequired");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append("expense_source", expense_source);
    formData.append("amount", amount);
    formData.append("date", date);

    handleUpdate(payment?.id, formData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ borderRadius: 2 }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          backgroundColor: "primary.main",
          color: "white",
          textAlign: "center",
          py: 2,
        }}
      >
        {t("finance.editExpensePayment")}
      </DialogTitle>
      <DialogContent sx={{ p: 4, backgroundColor: "#f9fafa" }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              {t("finance.expenseSource")}
            </Typography>
            <TextField
              select
              fullWidth
              variant="outlined"
              value={expense_source}
              onChange={(e) => setExpenseSource(e.target.value)}
              error={!!formErrors.expense_source}
              helperText={formErrors.expense_source}
            >
              {loadingExpenseSources ? (
                <MenuItem disabled>{t("finance.loadingCompany")}</MenuItem>
              ) : (
                sources.map((item, index) => (
                  <MenuItem key={index} value={item.id}>
                    {item?.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              {t("finance.amountPaid")}
            </Typography>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={!!formErrors.amount}
              helperText={formErrors.amount}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              {t("finance.paymentDate")}
            </Typography>
            <TextField
              fullWidth
              type="date"
              variant="outlined"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              error={!!formErrors.date}
              helperText={formErrors.date}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 4,
          py: 3,
          backgroundColor: "#f9fafa",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: "bold",
            textTransform: "none",
          }}
          disabled={loading}
        >
          {t("finance.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: "bold",
            textTransform: "none",
          }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("finance.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditExpensePaymentListDialog;
